import { isFunction } from 'lodash';
import { useLayoutEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
    Navigate,
    Outlet,
    RouteObject,
    RouterProvider,
    createBrowserRouter,
    useLocation,
    useMatches,
    useRouteError,
    useSearchParams,
} from 'react-router-dom';

import { QueryClientProvider, useQuery } from '@tanstack/react-query';

import Button from './components/Button.tsx';
import { ConfirmationDialogProvider } from './components/ConfirmationDialog.tsx';
import LinkButton from './components/LinkButton.tsx';
import { DEFAULT_BASE_COLOR, DEFAULT_THEME_COLOR, ThemeProvider, useTheme } from './components/Theme.tsx';
import { getErrorStatus } from './data/errors.ts';
import { eventDetailQuery, organizationDetailQuery, spaceDetailQuery, userDetailQuery } from './data/queries.ts';
import AdminLayout from './modules/admin/components/AdminLayout.tsx';
import { ADMIN_EVENT_LIST_PATH } from './modules/admin/events/paths.ts';
import { default as adminEventsRoutes } from './modules/admin/events/routes.tsx';
import { ADMIN_ORGANIZATION_PROFILE_LIST_PATH } from './modules/admin/organization-profiles/paths.ts';
import { default as adminOrganizationProfileRoutes } from './modules/admin/organization-profiles/routes.tsx';
import { default as adminOrganizationSettingsRoutes } from './modules/admin/organization-settings/routes.tsx';
import { default as adminOrganizationRoutes } from './modules/admin/organizations/routes.tsx';
import { default as adminSpaceSettingsRoutes } from './modules/admin/space-settings/routes.tsx';
import { ADMIN_USER_PATH } from './modules/admin/user-settings/paths.ts';
import { default as adminUserSettingsRoutes } from './modules/admin/user-settings/routes.tsx';
import { default as adminUserRoutes } from './modules/admin/users/routes.tsx';
import { ADMIN_LOGIN_PATH } from './modules/auth/paths.ts';
import { default as authRoutes } from './modules/auth/routes.tsx';
import { HOME_PATH } from './modules/site/paths.ts';
import { default as siteRoutes } from './modules/site/routes.tsx';
import { isCustomDomain, useEventId } from './modules/site/utils.ts';
import { ADMIN_ORGANIZATION_PATH, ADMIN_PATH } from './paths.ts';
import ensureQueriesData from './utils/ensureQueriesData.ts';
import getIsActiveRoute from './utils/getIsActiveRoute.ts';
import { useDocumentTitle } from './utils/htmlDocument.tsx';
import { authorized, authorizedLoader, isAdminUser, isOrganizationUser } from './utils/loaders.ts';
import { queryClient } from './utils/queryClient.ts';
import url from './utils/url.ts';

export const organizationAdminLoader = ({ params }: any) =>
    ensureQueriesData({
        user: userDetailQuery(),
        organization: organizationDetailQuery(),
    });

const AdminRedirect = () => {
    const { data: viewer } = useQuery(userDetailQuery());

    if (isAdminUser(viewer!)) {
        return <Navigate to={ADMIN_EVENT_LIST_PATH} replace={true} />;
    }

    if (isOrganizationUser(viewer!)) {
        return <Navigate to={ADMIN_ORGANIZATION_PATH} replace={true} />;
    }

    return <Navigate to={ADMIN_USER_PATH} replace={true} />;
};

interface DocumentHandle {
    title?: [string, object?] | ((loaderData: any) => [string, object?]);
}

const Document = () => {
    const { t } = useTranslation();

    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const matches = useMatches();
    const lastMatch = matches[matches.length - 1];

    const eventId = useEventId();
    const { data: event } = useQuery(eventDetailQuery(eventId!));
    const { data: space } = useQuery(spaceDetailQuery());

    const { setTheme, setBase } = useTheme();

    const isAdmin = pathname.startsWith('/admin');

    const { title } = (lastMatch?.handle || {}) as DocumentHandle;

    let [translationKey, translationParams] = (isFunction(title) ? title(lastMatch?.data) : title) || [];

    const translatedTitle = t(translationKey, translationParams as any);
    useDocumentTitle(`${translatedTitle} - ${event?.name || space?.title || space?.name || ''}`);

    useLayoutEffect(() => {
        if (!isAdmin) {
            if (event?.themeColorName) setTheme(event.themeColorName);
            if (event?.baseColorName) setBase(event.baseColorName);
        } else {
            setTheme(DEFAULT_THEME_COLOR);
            setBase(DEFAULT_BASE_COLOR);
        }
    }, [event, isAdmin]);

    const parseAndActivate = (html: string): DocumentFragment => {
        const tpl = document.createElement('template');
        tpl.innerHTML = html;
        tpl.content.querySelectorAll('script').forEach((orig) => {
            const script = document.createElement('script');
            Array.from(orig.attributes).forEach((a) => script.setAttribute(a.name, a.value));
            script.textContent = orig.textContent;
            orig.replaceWith(script);
        });
        return tpl.content;
    };

    useLayoutEffect(() => {
        if (isAdmin) return;

        const cleanups: (() => void)[] = [];

        const inject = (target: HTMLElement, html: string) => {
            const frag = parseAndActivate(html);
            const nodes = Array.from(frag.childNodes);
            target.append(frag);
            cleanups.push(() => nodes.forEach((n) => n.parentNode?.removeChild(n)));
        };

        if (event?.headHtml) inject(document.head, event.headHtml);
        if (event?.bodyHtml) inject(document.body, event.bodyHtml);

        return () => cleanups.forEach((fn) => fn());
    }, [event?.headHtml, event?.bodyHtml, isAdmin]);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname, searchParams.get('page')]);

    return <Outlet />;
};

const NotFound = () => (
    <div className="max-w-[80%] max-h-[60%] bg-contain bg-center h-full w-full flex flex-col gap-10 items-center">
        <div
            className="bg-contain bg-center h-full w-full mix-blend-multiply bg-no-repeat"
            style={{ backgroundImage: "url('/images/error-not-found.png')" }}
        />

        <div className="flex flex-col gap-4 items-center">
            <span className="text-base-600">Fehlercode 404</span>
            <span className="text-base-900 font-semibold text-xl">Die Seite konnte nicht gefunden werden</span>
        </div>

        <LinkButton to={url(getIsActiveRoute(ADMIN_PATH) ? ADMIN_PATH : HOME_PATH)}>Zurück zur Startseite</LinkButton>
    </div>
);

const Forbidden = () => (
    <div className="max-w-[80%] max-h-[60%] bg-contain bg-center h-full w-full flex flex-col gap-10 items-center">
        <div
            className="bg-contain bg-center h-full w-full mix-blend-multiply bg-no-repeat"
            style={{ backgroundImage: "url('/images/error-forbidden.png')" }}
        />

        <div className="flex flex-col gap-4 items-center">
            <span className="text-base-600">Fehlercode 403</span>
            <span className="text-base-900 font-semibold text-xl">Du hast keinen Zugriff auf diese Seite</span>
        </div>

        <LinkButton to={url(getIsActiveRoute(ADMIN_PATH) ? ADMIN_PATH : HOME_PATH)}>Zurück zur Startseite</LinkButton>
    </div>
);

const ErrorBoundary = () => {
    const error = useRouteError();
    const status = getErrorStatus(error);
    const { t } = useTranslation('components');
    const location = useLocation();

    if (status === 401 && location.pathname !== ADMIN_LOGIN_PATH) {
        const search = new URLSearchParams({ next: location.pathname + location.search });
        return <Navigate to={`${url(ADMIN_LOGIN_PATH)}?${search}`} replace={true} />;
    }

    return (
        <div className="antialiased">
            <div className="h-screen flex items-center justify-center p-6">
                {status === 404 ? (
                    <NotFound />
                ) : status === 403 ? (
                    <Forbidden />
                ) : (
                    <div role="alert" className="flex flex-col items-center gap-4 text-center max-w-md">
                        <h1 className="text-2xl font-semibold">{t('requestError.title')}</h1>
                        <p>{t('requestError.description')}</p>
                        <Button onClick={() => window.location.reload()}>{t('requestError.retry')}</Button>
                    </div>
                )}
            </div>
        </div>
    );
};

const userLoader = () =>
    ensureQueriesData({
        user: userDetailQuery(),
        organization: organizationDetailQuery(),
        event: eventDetailQuery(),
        space: spaceDetailQuery(),
    });

const transformRoutes = (routes: any): RouteObject[] => {
    return routes.map((route: RouteObject) => ({
        ...route,
        // second case is only for initial route
        path: route.path?.replace('/events/:eventId/', '/').replace('/events/:eventId', '/'),
        children: route.children ? transformRoutes(route.children) : route.children,
    }));
};

const AppRouter = () => {
    const router = createBrowserRouter([
        {
            element: <Document />,
            loader: userLoader,
            errorElement: <ErrorBoundary />,
            children: [
                {
                    path: ADMIN_PATH,
                    element: <AdminRedirect />,
                    loader: authorized(),
                },
                {
                    element: <AdminLayout />,
                    loader: authorizedLoader(organizationAdminLoader),
                    children: [
                        {
                            path: ADMIN_ORGANIZATION_PATH,
                            loader: authorized(isOrganizationUser),
                            children: [
                                {
                                    index: true,
                                    element: <Navigate to={ADMIN_ORGANIZATION_PROFILE_LIST_PATH} replace={true} />,
                                },
                                ...adminOrganizationProfileRoutes,
                                ...adminOrganizationSettingsRoutes,
                            ],
                        },
                        ...adminUserSettingsRoutes,
                        ...adminSpaceSettingsRoutes,
                        ...adminEventsRoutes,
                        ...adminOrganizationRoutes,
                        ...adminUserRoutes,
                    ],
                },

                ...authRoutes,

                ...(isCustomDomain() ? transformRoutes(siteRoutes) : siteRoutes),

                {
                    path: '*',
                    loader: () => {
                        throw new Response('Not Found', { status: 404, statusText: 'Not Found' });
                    },
                },
            ],
        },
    ] as RouteObject[]);

    return <RouterProvider router={router} />;
};

const App = () => {
    return (
        <>
            <ConfirmationDialogProvider>
                <QueryClientProvider client={queryClient}>
                    <ThemeProvider>
                        <AppRouter />
                    </ThemeProvider>
                </QueryClientProvider>
            </ConfirmationDialogProvider>

            <Toaster toastOptions={{ className: 'toast' }} />
        </>
    );
};

export default App;
